import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../styles/global.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://zippy-flexibility-production.up.railway.app';

export default function GalleryView() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | notfound | error
  const [showInstall, setShowInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [saving, setSaving] = useState(''); // key of the item currently being saved ('' = idle)
  const [hint, setHint] = useState('');
  const [playing, setPlaying] = useState(null); // index of the video playing inline
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const photoFilesRef = useRef(null); // cached photo File[] once prepared

  useEffect(() => {
    axios.get(`${API_URL}/sessions/gallery/${token}`)
      .then(res => { setSession(res.data); setStatus('ready'); })
      .catch(err => setStatus(err.response?.status === 404 ? 'notfound' : 'error'));
  }, [token]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isStandalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);
  const downloadUrl = `${API_URL}/sessions/gallery/${token}/download`;

  // Can this device share files (i.e. offer a native "Save / Share")?
  const canShareFiles = (() => {
    try { return typeof navigator !== 'undefined' && !!navigator.canShare && navigator.canShare({ files: [new File(['x'], 'x.jpg', { type: 'image/jpeg' })] }); }
    catch (_) { return false; }
  })();

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const thumb = (id) => `https://drive.google.com/thumbnail?id=${id}&sz=w1200`;

  const videos = session?.files?.filter(f => f.file_type?.startsWith('video')) || [];
  const images = session?.files?.filter(f => f.file_type?.startsWith('image')) || [];

  const fetchAsFile = async (file) => {
    const resp = await fetch(`${API_URL}/sessions/gallery/${token}/file/${file.drive_file_id}`);
    const blob = await resp.blob();
    return new File([blob], file.file_name, { type: file.file_type || blob.type });
  };

  // Save one item to the camera roll via the native share sheet (falls back to a download on desktop).
  const saveOne = async (file, key) => {
    if (saving) return;
    setSaving(key);
    try {
      const f = await fetchAsFile(file);
      if (canShareFiles && navigator.canShare({ files: [f] })) {
        await navigator.share({ files: [f] });
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(f); a.download = file.file_name;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
    } catch (_) { /* user cancelled or an error occurred */ }
    setSaving('');
  };

  // Save all photos at once via the native share sheet ("Save N Images" -> camera roll).
  // First tap fetches every photo (a few seconds); once they're ready the share sheet
  // opens. If iOS dropped the tap during the wait, the photos stay cached so a second
  // tap opens the sheet instantly.
  const saveAllPhotos = async () => {
    if (photoFilesRef.current) {
      try { await navigator.share({ files: photoFilesRef.current }); } catch (_) {}
      return;
    }
    if (saving) return;
    setSaving('all');
    setHint('');
    try {
      const files = [];
      for (const img of images) files.push(await fetchAsFile(img));
      photoFilesRef.current = files;
      if (canShareFiles && navigator.canShare({ files })) {
        try {
          await navigator.share({ files });
        } catch (_) {
          setHint("Photos ready — tap ‘Save All Photos’ again to save them to your camera roll.");
        }
      }
    } catch (_) { setHint('Sorry, that didn’t work. Try “Save / Share” on each photo instead.'); }
    setSaving('');
  };

  const Shell = ({ children }) => (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="app-header">
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal)' }}>
          BUMP <span style={{ color: '#e8836a' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
        </div>
      </header>
      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px', width: '100%', flex: 1 }}>{children}</div>
      <footer className="app-footer">
        <p>Made with <span className="heart">&#x2665;&#xFE0E;</span> by <strong>Bump Baby Imaging</strong></p>
        <p style={{ marginTop: '4px' }}>info@bumpbabyimaging.com.au</p>
      </footer>
    </div>
  );

  if (status === 'loading') return <Shell><div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-soft)' }}>Loading your gallery...</div></Shell>;
  if (status === 'notfound') return <Shell><div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-soft)' }}>This gallery link is no longer available. Please contact us at info@bumpbabyimaging.com.au.</div></Shell>;
  if (status === 'error') return <Shell><div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-soft)' }}>Something went wrong loading this gallery. Please try again shortly.</div></Shell>;

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviteBusy(true); setInviteMsg('');
    try {
      const res = await axios.post(`${API_URL}/sessions/gallery/${token}/invite`, { email: inviteEmail });
      setInviteMsg(res.data.message || 'Invite sent!');
      setInviteEmail('');
    } catch (err) {
      setInviteMsg(err.response?.data?.error || 'Could not send invite. Please try again.');
    } finally { setInviteBusy(false); }
  };

  const handleAddToHome = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch (_) {}
      setDeferredPrompt(null);
    } else {
      setShowInstall(true);
    }
  };

  const saveBtnStyle = { background: 'var(--teal-light)', color: 'var(--teal)', border: 'none', borderRadius: '8px', padding: '7px 12px', fontSize: '12px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' };

  return (
    <Shell>
      {/* Greeting */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--teal-dark)' }}>
          Hello {session.first_name}! &#x2665;&#xFE0E;
        </div>
        <div style={{ fontSize: '14px', color: 'var(--text-soft)', marginTop: '6px' }}>
          {session.session_type} &middot; {formatDate(session.session_date)}
          {session.expires_at && <span style={{ color: 'var(--coral)', marginLeft: '8px' }}>&middot; Available until {formatDate(session.expires_at)}</span>}
        </div>
        <div style={{ background: '#fdeee9', borderRadius: '12px', padding: '14px 16px', marginTop: '16px', fontSize: '13px', color: '#c96a52', lineHeight: 1.6 }}>
          <strong>Save your favourites to keep them forever.</strong>{' '}
          {canShareFiles ? (
            <>Tap <strong>Save All Photos</strong> to add every photo to your camera roll at once. For videos, tap <strong>Save / Share</strong> on each one (they're large, so saving one at a time works best). On computers, <strong>Download All</strong> gives you a zip of everything.</>
          ) : (
            <>Use <strong>Download All</strong> to save everything to your computer as a zip. On a phone, open this gallery and tap <strong>Save / Share</strong> to add items straight to your camera roll.</>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px', flexWrap: 'wrap' }}>
          {canShareFiles && images.length > 0 && (
            <button onClick={saveAllPhotos} disabled={!!saving} style={{ background: 'var(--coral)', color: 'white', border: 'none', borderRadius: '100px', padding: '12px 26px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: saving ? 'default' : 'pointer' }}>
              {saving === 'all' ? 'Preparing…' : 'Save All Photos'}
            </button>
          )}
          {session.files?.length > 0 && (
            <a href={downloadUrl} style={{ background: canShareFiles ? 'white' : 'var(--coral)', color: canShareFiles ? 'var(--teal)' : 'white', border: canShareFiles ? '2px solid var(--border)' : 'none', borderRadius: '100px', padding: canShareFiles ? '10px 22px' : '12px 26px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              &#8595; Download All
            </a>
          )}
          {!isStandalone && (
            <button onClick={handleAddToHome} style={{ background: 'white', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '100px', padding: '10px 22px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              &#43; Add to Home Screen
            </button>
          )}
          <button onClick={() => { setShowInvite(true); setInviteMsg(''); }} style={{ background: 'white', color: 'var(--teal)', border: '2px solid var(--border)', borderRadius: '100px', padding: '10px 22px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#e8836a' }}>&#x2665;&#xFE0E;</span> Invite Family
          </button>
        </div>
      </div>

      {/* Videos */}
      {videos.length > 0 && (
        <>
          <div className="section-label">Video{videos.length > 1 ? 's' : ''}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', marginBottom: '32px' }}>
            {videos.map((video, i) => (
              <div key={i} style={{ background: 'white', borderRadius: '14px', overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 2px 10px rgba(83,147,144,0.08)' }}>
                {playing === i ? (
                  <div style={{ aspectRatio: '16/9', background: '#000' }}>
                    <iframe title={`Video ${i + 1}`} src={`https://drive.google.com/file/d/${video.drive_file_id}/preview`} allow="autoplay; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />
                  </div>
                ) : (
                  <div style={{ background: 'linear-gradient(135deg, var(--teal-dark), var(--teal))', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    onClick={() => setPlaying(i)}>
                    <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.95)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'var(--teal)' }}>&#9654;</div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>Video</div>
                  <button onClick={() => saveOne(video, 'v' + i)} disabled={!!saving} style={saveBtnStyle}>
                    {saving === 'v' + i ? 'Preparing…' : (canShareFiles ? '↓ Save / Share' : '↓ Save')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Images */}
      {images.length > 0 && (
        <>
          <div className="section-label">Photos</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px', marginBottom: '32px' }}>
            {images.map((image, i) => (
              <div key={i} style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                <div style={{ aspectRatio: '1', background: 'var(--teal-light)', cursor: 'pointer', overflow: 'hidden' }}
                  onClick={() => window.open(image.drive_view_link, '_blank')}>
                  <img src={thumb(image.drive_file_id)} alt={image.file_name} loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.visibility = 'hidden'; }} />
                </div>
                <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <button onClick={() => saveOne(image, 'i' + i)} disabled={!!saving} style={{ ...saveBtnStyle, width: '100%' }}>
                    {saving === 'i' + i ? 'Preparing…' : (canShareFiles ? '↓ Save / Share' : '↓ Save')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {videos.length === 0 && images.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-soft)' }}>Your photos are being added and will appear here shortly.</div>
      )}

      {saving && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: 'var(--teal-dark)', color: 'white', padding: '12px 24px', borderRadius: '100px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', boxShadow: '0 8px 24px rgba(45,100,100,0.3)', zIndex: 300 }}>
          Preparing your {saving === 'all' ? 'photos' : 'file'}…
        </div>
      )}

      {hint && !saving && (
        <div onClick={() => setHint('')} style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: 'var(--teal-dark)', color: 'white', padding: '12px 20px', borderRadius: '16px', fontSize: '13px', fontWeight: 600, fontFamily: 'Nunito, sans-serif', boxShadow: '0 8px 24px rgba(45,100,100,0.3)', zIndex: 300, maxWidth: '90%', width: '360px', textAlign: 'center', cursor: 'pointer', lineHeight: 1.5 }}>
          {hint}
        </div>
      )}

      {showInvite && (
        <div onClick={() => setShowInvite(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(45,100,100,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', width: '100%' }}>
            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal-dark)', marginBottom: '8px' }}>Invite Family <span style={{ color: '#e8836a' }}>&#x2665;&#xFE0E;</span></h3>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', lineHeight: 1.6, marginBottom: '16px' }}>
              Enter a family member's email and they'll receive a link to view this gallery. They won't be able to see any other galleries.
            </p>
            {inviteMsg && <div style={{ fontSize: '13px', color: 'var(--teal-dark)', background: 'var(--teal-light)', padding: '10px 14px', borderRadius: '10px', marginBottom: '12px' }}>{inviteMsg}</div>}
            <form onSubmit={sendInvite} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input type="email" required placeholder="grandma@email.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} style={{ padding: '12px 14px', border: '2px solid var(--border)', borderRadius: '10px', fontSize: '14px', color: 'var(--text)', outline: 'none', fontFamily: 'Nunito, sans-serif' }} />
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowInvite(false)} style={{ background: 'var(--teal-light)', color: 'var(--teal)', border: 'none', borderRadius: '10px', padding: '11px 20px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>Close</button>
                <button type="submit" disabled={inviteBusy} style={{ background: 'var(--coral)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 22px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: inviteBusy ? 'default' : 'pointer' }}>{inviteBusy ? 'Sending…' : 'Send Invite'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showInstall && (
        <div onClick={() => setShowInstall(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(45,100,100,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', width: '100%' }}>
            <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal-dark)', marginBottom: '10px' }}>Add to Home Screen</h3>
            <p style={{ fontSize: '14px', color: 'var(--text-soft)', lineHeight: 1.6, marginBottom: '16px' }}>
              Keep your gallery one tap away, like an app.
            </p>
            {isIOS ? (
              <>
                <div style={{ background: '#fdeee9', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: '#c96a52' }}>
                  This only works in <strong>Safari</strong>. If you opened this from an email or another app, first tap the browser menu and choose <strong>Open in Safari</strong>.
                </div>
                <ol style={{ fontSize: '14px', color: 'var(--teal-dark)', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
                  <li>Tap the <strong>Share</strong> button (the square with an up-arrow) at the bottom of Safari.</li>
                  <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
                  <li>Tap <strong>Add</strong>.</li>
                </ol>
              </>
            ) : (
              <ol style={{ fontSize: '14px', color: 'var(--teal-dark)', lineHeight: 1.8, paddingLeft: '20px', margin: 0 }}>
                <li>Tap the <strong>&#8942;</strong> menu (top-right of Chrome).</li>
                <li>Tap <strong>Add to Home screen</strong> (or <strong>Install app</strong>).</li>
                <li>Tap <strong>Add</strong> / <strong>Install</strong>.</li>
              </ol>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setShowInstall(false)} style={{ background: 'var(--teal)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
